// Planning des examens et des rattrapages, seen by a professor (read-only).
// Lists the published exam / rattrapage sessions on the courses they teach —
// or where they proctor — grouped by day. The server applies the ownership
// rule (GET /professor/exams); the page only splits exams from rattrapages.
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, Building2, CalendarDays, Clock, Loader2, MapPin, RotateCcw, Users } from 'lucide-react';
import { useMyExams } from '../hooks/useProfessor';
import { fetchAcademicYears, type AcademicYear } from '@/services/academicPlanningService';
import type { ProfessorExamInfo } from '@/types/professor.types';

type Filter = 'all' | 'exams' | 'rattrapages';

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'Tous' },
  { key: 'exams', label: 'Examens' },
  { key: 'rattrapages', label: 'Rattrapages' },
];

const filterFromParam = (value: string | null): Filter =>
  value === 'exams' || value === 'rattrapages' ? value : 'all';

const dayLabel = (iso: string): string =>
  new Date(iso).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

const TeacherExamsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  // Deep links from the sidebar preset the filter (?type=exams|rattrapages).
  const [filter, setFilter] = useState<Filter>(filterFromParam(searchParams.get('type')));
  useEffect(() => {
    setFilter(filterFromParam(searchParams.get('type')));
  }, [searchParams]);

  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]);
  const [academicYearId, setAcademicYearId] = useState<string | undefined>(undefined);
  useEffect(() => {
    fetchAcademicYears()
      .then(setAcademicYears)
      .catch(() => setAcademicYears([]));
  }, []);

  const { data: items = [], isLoading, error, refetch } = useMyExams(academicYearId);

  const visible = useMemo(() => {
    if (filter === 'exams') return items.filter((e) => !e.isRattrapage);
    if (filter === 'rattrapages') return items.filter((e) => e.isRattrapage);
    return items;
  }, [items, filter]);

  // Grouped by day, days in chronological order (the server already sorts).
  const byDay = useMemo(() => {
    const map = new Map<string, ProfessorExamInfo[]>();
    for (const item of visible) {
      const key = item.date.slice(0, 10);
      map.set(key, [...(map.get(key) ?? []), item]);
    }
    return Array.from(map.entries());
  }, [visible]);

  const counts = useMemo(
    () => ({
      all: items.length,
      exams: items.filter((e) => !e.isRattrapage).length,
      rattrapages: items.filter((e) => e.isRattrapage).length,
    }),
    [items],
  );

  const emptyText =
    filter === 'rattrapages'
      ? 'Aucun rattrapage planifié pour vos cours.'
      : filter === 'exams'
        ? 'Aucun examen planifié pour vos cours.'
        : 'Aucun examen ni rattrapage planifié pour vos cours.';

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <CalendarDays className="h-6 w-6 text-blue-600" />
              Examens & rattrapages
            </h1>
            <p className="text-gray-500 mt-1">
              Sessions publiées pour vos cours. Consultation uniquement — la planification est faite par l'administration.
            </p>
          </div>
          <select
            value={academicYearId ?? ''}
            onChange={(e) => setAcademicYearId(e.target.value || undefined)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            aria-label="Année académique"
          >
            <option value="">Année en cours</option>
            {academicYears.map((year) => (
              <option key={year.id} value={year.id}>
                {year.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-2 mt-5">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                filter === f.key
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {f.label} ({counts[f.key]})
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Chargement…
        </div>
      )}

      {error && !isLoading && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center justify-between">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="h-5 w-5" /> Impossible de charger le planning.
          </div>
          <button
            type="button"
            onClick={() => void refetch()}
            className="px-3 py-1.5 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700"
          >
            Réessayer
          </button>
        </div>
      )}

      {!isLoading && !error && byDay.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 py-16 text-center">
          <CalendarDays className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">{emptyText}</p>
          <p className="text-gray-500 text-sm mt-1">
            Les sessions apparaissent ici dès que l'administration publie le planning.
          </p>
        </div>
      )}

      {!isLoading &&
        !error &&
        byDay.map(([day, sessions]) => (
          <section key={day} className="bg-white rounded-xl shadow-sm border border-gray-200">
            <h2 className="px-6 py-3 border-b border-gray-100 font-semibold text-gray-900 capitalize">
              {dayLabel(day)}
            </h2>
            <ul className="divide-y divide-gray-100">
              {sessions.map((s) => (
                <li key={s.id} className="px-6 py-4 flex flex-col md:flex-row md:items-center gap-3">
                  <div
                    className={`shrink-0 w-24 text-center rounded-lg py-2 text-sm font-semibold ${
                      s.isRattrapage ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'
                    }`}
                  >
                    <Clock className="h-4 w-4 inline mr-1 -mt-0.5" />
                    {s.startTime}–{s.endTime}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-900">{s.moduleElementName}</span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          s.isRattrapage ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {s.isRattrapage ? (
                          <>
                            <RotateCcw className="h-3 w-3 inline mr-1 -mt-0.5" />
                            Rattrapage
                          </>
                        ) : (
                          'Examen'
                        )}
                      </span>
                      {!s.isOwnClass && s.isProctor && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                          Surveillance
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {s.groupeName}
                        {s.filiereCode ? ` · ${s.filiereCode}` : ''}
                      </span>
                      {s.roomName && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          {s.roomName}
                        </span>
                      )}
                      {s.campusName && (
                        <span className="flex items-center gap-1">
                          <Building2 className="h-4 w-4" />
                          {s.campusName}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
    </div>
  );
};

export default TeacherExamsPage;
